import React from 'react';
import styled from 'styled-components';
import {D} from './design-tokens';

// ── SmallBtn ──────────────────────────────────────────────────────────────────

type SmallBtnProps = {
	children: React.ReactNode;
	onClick: () => void;
};

const SmallBtnEl = styled.button`
	padding: 5px 16px;
	font-size: 12px;
	cursor: pointer;
	border-radius: 6px;
	background: ${D.surface2};
	border: 1px solid ${D.userBubbleBorder};
	color: ${D.accentMid};
	font-weight: 600;
	transition: background 0.15s, border-color 0.15s;

	&:hover {
		background: ${D.userBubbleBorder};
		border-color: ${D.accentMid};
	}
`;

export function SmallBtn({children, onClick}: SmallBtnProps): JSX.Element {
	return (
		<SmallBtnEl type="button" onClick={onClick}>
			{children}
		</SmallBtnEl>
	);
}

// ── ThinkingDots ──────────────────────────────────────────────────────────────

const DotsWrapper = styled.div`
	display: flex;
	gap: 4px;
	padding: 3px 0;
`;

const Dot = styled.div<{delay: number}>`
	width: 5px;
	height: 5px;
	border-radius: 50%;
	background: ${D.accentMid};
	opacity: 0.4;
	animation: ai-bounce 1.2s ${({delay}) => delay}s infinite;
`;

export function ThinkingDots(): JSX.Element {
	return (
		<DotsWrapper>
			{[0, 1, 2].map(i => (
				<Dot key={i} delay={i * 0.2} />
			))}
		</DotsWrapper>
	);
}

// ── CheckDot ──────────────────────────────────────────────────────────────────

export function CheckDot(): JSX.Element {
	return (
		<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
			<circle cx="5" cy="5" r="4.5" fill={D.stepDoneBorder} />
			<path
				d="M3 5l1.5 1.5L7 3.5"
				stroke={D.stepDoneText}
				strokeWidth="1.2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// ── SpinnerDot ────────────────────────────────────────────────────────────────

const SpinnerSvg = styled.svg`
	animation: ai-spin 0.8s linear infinite;
`;

export function SpinnerDot(): JSX.Element {
	return (
		<SpinnerSvg width="10" height="10" viewBox="0 0 10 10" fill="none">
			<circle cx="5" cy="5" r="4" stroke={D.stepActiveBorder} strokeWidth="1.5" strokeDasharray="8 14" />
		</SpinnerSvg>
	);
}

// ── IdleDot ───────────────────────────────────────────────────────────────────

const IdleDotEl = styled.span`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: ${D.stepIdleDot};
	display: inline-block;
	flex-shrink: 0;
`;

export function IdleDot(): JSX.Element {
	return <IdleDotEl />;
}

// ── SparkleIcon ───────────────────────────────────────────────────────────────

type SparkleIconProps = {
	size?: number;
	color?: string;
};

export function SparkleIcon({size = 16, color = 'currentColor'}: SparkleIconProps): JSX.Element {
	return (
		<svg width={size} height={size} viewBox="0 0 16 16" fill="none">
			<path d="M8 2l1.2 3.8H13L9.9 7.9l1.2 3.8L8 9.6l-3.1 2.1 1.2-3.8L3 5.8h3.8L8 2z" fill={color} />
		</svg>
	);
}

// ── CloseIcon ─────────────────────────────────────────────────────────────────

export function CloseIcon(): JSX.Element {
	return (
		<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
			<path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
		</svg>
	);
}

// ── SendIcon ──────────────────────────────────────────────────────────────────

type SendIconProps = {
	active: boolean;
};

export function SendIcon({active}: SendIconProps): JSX.Element {
	return (
		<svg width="13" height="13" viewBox="0 0 13 13" fill="none">
			<path d="M1 12L12 6.5 1 1v4.5l7 2-7 2V12z" fill={active ? '#ffffff' : D.textDisabled} />
		</svg>
	);
}

// ── StopIcon ──────────────────────────────────────────────────────────────────

export function StopIcon(): JSX.Element {
	return (
		<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
			<rect x="1.5" y="1.5" width="7" height="7" rx="1" fill={D.errorText} />
		</svg>
	);
}
